import classNames from "classnames";
import { CardVariant, MDOption, MetadataType } from "types";
import { useRef } from "react";
import LabelRowWithOverflowCount from "./LabelsWithOverflow";
import { formatUnderscoreWithSpace } from "helpers/utility/formatters";

import styles from "./style.module.scss";
import { ConfigProvider, Popover } from "antd";
import { CardMetadata } from "../../pages/DataPage/CardsView/DataList/EnrichmentCard/CardMetadata/CardMetadata";
import VLTooltip from "../../uikit/VLTooltip";
import { CardHeaderProps } from "../../pages/DataPage/CardsView/DataList/EnrichmentCard/CardHeader";
import { CardHeaderSuffix } from "../../pages/DataPage/CardsView/DataList/EnrichmentCard/CardHeader/HeaderTitle";

export interface CardMetadataLabelsProps {
  labels: string[];
  options?: MDOption[];
  metadataType: MetadataType;
  cardVariant: CardVariant;
  labelIcon: JSX.Element;
  labelType?: string;
  selectedLabels?: string[];
  onLabelSelection?: (label: string) => void;
  suffix?: CardHeaderProps["suffix"];
  interactive?: boolean;
}

const CardMetadataLabels = ({
  labels,
  metadataType,
  cardVariant,
  labelIcon,
  selectedLabels = [],
  onLabelSelection,
  suffix,
  labelType,
  interactive = true,
}: CardMetadataLabelsProps) => {
  const filterBtnRef = useRef<HTMLDivElement>(null);

  const popupRootRef = useRef<HTMLDivElement>(null);
  const mountPopup = () => popupRootRef.current ?? document.body;

  const name =
    metadataType === MetadataType.OBJECT_LABELS
      ? "Objects"
      : formatUnderscoreWithSpace(
          metadataType.toLowerCase().replace("image", "").replace("object", ""),
        );
  const noLabelsText = `No ${name} available`;

  const filterClassnames = classNames(
    styles.metadataFilter,
    styles.noDetailsClick,
    {
      [styles.interactive]: interactive,
      [styles.objectCaptions]: metadataType === MetadataType.OBJECT_CAPTIONS,
      [styles.imageCaptions]: metadataType === MetadataType.IMAGE_CAPTIONS,
      [styles.objectLabels]: metadataType === MetadataType.OBJECT_LABELS,
      [styles.imageLabels]: metadataType === MetadataType.IMAGE_LABELS,
      [styles.userTags]: metadataType === MetadataType.USER_TAGS,
      [styles.similarityMetadataFilter]: cardVariant === CardVariant.VERTEX,
      [styles.exportMetadataFilter]: cardVariant === CardVariant.SELECTED,
      [styles.onlyIcon]: true,
      [styles.unsetWidth]: Boolean(labelType),
    },
  );

  const noLabelsClassname = classNames(styles.noLabelsText, {
    [styles.similarityText]: cardVariant === CardVariant.VERTEX,
    [styles.exportText]: cardVariant === CardVariant.SELECTED,
  });

  const renderButton = () => (
    <div
      ref={filterBtnRef}
      className={filterClassnames}
      data-testid="metadata-filter"
    >
      <div className={styles.labelIcon}>
        {labelIcon}
        {labelType}
      </div>
    </div>
  );

  const emptyMessage = (
    <span className={noLabelsClassname}>{noLabelsText}</span>
  );

  return (
    <div
      className={classNames(styles.metadataContainer, {
        [styles.withSuffix]: Boolean(suffix),
      })}
      onClick={(e) => {
        e.stopPropagation();
      }}
      ref={popupRootRef}
    >
      <ConfigProvider
        theme={{
          components: { Popover: { colorBgElevated: "transparent" } },
        }}
      >
        <Popover
          placement="bottomLeft"
          trigger="click"
          getPopupContainer={mountPopup}
          content={
            interactive && (
              <CardMetadata
                onLabelSelection={onLabelSelection}
                emptyMessage={emptyMessage}
                metadataType={metadataType}
              />
            )
          }
          arrow={false}
          styles={{ container: { padding: "12px 0 0 12px" } }}
        >
          <>
            <VLTooltip
              title={interactive && name}
              mouseEnterDelay={0}
              className={styles.tooltip}
            >
              {renderButton()}
            </VLTooltip>
          </>
        </Popover>
      </ConfigProvider>

      <div
        className={classNames(
          styles.labelsSection,
          styles.labelsSectionWithoutDropdown,
        )}
      >
        {labels.length ? (
          <LabelRowWithOverflowCount
            labels={labels}
            cardVariant={cardVariant}
            selectedLabels={selectedLabels}
            onLabelSelection={onLabelSelection}
          />
        ) : (
          emptyMessage
        )}
      </div>
      {suffix && <CardHeaderSuffix {...suffix} />}
    </div>
  );
};

export default CardMetadataLabels;
